import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";

import { LngLatBoundsLike } from "maplibre-gl";
import { Geometry } from "geojson";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CloseIcon,
  CompareTableIcon
} from "@/assets/icons";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import {
  CATCHMENT_SOURCE_IDS,
  COORDINATE_LABLE,
  UNIQUE_ID_PROPERTY
} from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  BOUNDS_PADDING,
  TO_BOUNDS_DURATION
} from "@/hooks/map/interactions/useMapInteractions";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setSelectedDataset } from "@/store/slice/datasetSlice";
import {
  setComparisonFeature,
  setSelectedFeature
} from "@/store/slice/mapSlice";
import {
  resetUIDataConfigExceptSidebarMenu,
  setShowComparisonPopup,
  toggleSpatialAnalysisResultTabularModal
} from "@/store/slice/uiSlice";
import {
  DataSchemaType,
  Dataset,
  DatasetDataType,
  DatasetSchemaLabel,
  SchemaToDatasetMap
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { EOutputAnalysisType } from "@/types/spatial";
import {
  getAvailableDatasets,
  getGeometryAttributeInsertIndex,
  resolveFeatureGeometryWKT,
  transformPropertiesWithCategory,
  transformVisibleAttributes
} from "@/utils/dataset";
import { hideAllLayersWithPrefixes } from "@/utils/map";
import { parseFeaturePhotoValue } from "@/utils/string";

import { bbox } from "@turf/turf";
import { Button } from "equator-design-system";

import FeatureInfoItem from "./FeatureInfoItem";
import { ParsedFeaturePhotoValue } from "./types";
import {
  buildAdminAreaAttribute,
  findItemById,
  getAttributePriority,
  getPhotoTypePriority,
  groupAttributesByPriority,
  GroupedAttributes,
  sortPrimaryAttributes
} from "./utils";

export interface SelectedAttribute {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
}

interface FeatureInfoProps {
  selectedFeature: SelectedFeature | null;
  setSelectedAttribute: (params: SelectedAttribute | null) => void;
}

type PhotoCandidate = {
  key: string;
  parsed: ParsedFeaturePhotoValue;
  orderIndex: number;
  originalIndex: number;
  typePriority: number;
};

const excludedKeys = new Set([
  "point_count",
  "point_count_abbreviated",
  UNIQUE_ID_PROPERTY,
  "cluster",
  "color",
  "opacity",
  "width"
]);

const adminAreaKeys = [
  "province_name",
  "city_name",
  "district_name",
  "village_name"
];

const ADMIN_AREA_LABEL = new Set([
  "Village Name",
  "District Name",
  "City Name",
  "Province Name"
]);

export const FeatureInfo: React.FC<FeatureInfoProps> = ({
  selectedFeature,
  setSelectedAttribute
}: FeatureInfoProps) => {
  const dispatch = useDispatch();
  const selectedFeatureRef = useRef(selectedFeature);
  const panelRef = useRef<HTMLDivElement>(null);
  const { isPOIInfoEnabled, isTabularViewAnalysisResultEnabled } =
    useAppFeatures();

  const {
    map,
    handleClearSelectedState,
    zoomLevel,
    hideAllMarkers,
    showOnlyLayer,
    deleteAllFeatures
  } = useMap();

  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );

  const { showAnalysisResultPopup, showAnalysisSummaryPopup } = useSelector(
    (state: RootState) => state.ui
  );

  const availableDatasets = getAvailableDatasets(datasets);
  const orderedDatasets = useMemo(
    () => visualizationOrder.map((id) => datasets[id]).filter(Boolean),
    [visualizationOrder, datasets]
  );

  const spatialAnalysisDatasetId =
    orderedDatasets.find(
      (dataset) => dataset?.data?.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
    )?.data?.id ?? null;
  const spatialAnalysisRawDataset =
    spatialAnalysisDatasetId && rawDatasets
      ? rawDatasets[spatialAnalysisDatasetId]
      : undefined;
  const isSiteProfiling =
    spatialAnalysisRawDataset?.spatial_aggregation_type ===
    EOutputAnalysisType.PROFILING;

  const { data: userProfile } = useGetUserProfileAPI();

  const [expanded, setExpanded] = useState(false);

  const currentDataset = findItemById(
    availableDatasets,
    selectedFeature?.source
  );
  const sourceDataset = rawDatasets?.[selectedFeature?.source ?? ""];

  // The features no longer carry the WKT (~12 KB per row on a boundary dataset).
  // Read it from the rows for the open feature only, memoised — resolving scans
  // the rows, and 11k of them is not work to repeat on every render.
  const sourceGeometry = useMemo(
    () =>
      resolveFeatureGeometryWKT(
        sourceDataset,
        // Properties, not `id`: `promoteId: "unique_id"` makes the clicked id the
        // feature index, not the id the collection was built with. Same schema as
        // the layer, so the id and geometry columns resolve the same way.
        selectedFeature?.properties,
        currentDataset?.categories
      ),
    [sourceDataset, selectedFeature?.properties, currentDataset?.categories]
  );

  useEffect(() => {
    // Update ref to current feature
    if (selectedFeature) {
      selectedFeatureRef.current = selectedFeature;
    }
    if (!selectedFeatureRef.current || selectedFeature) return;
    handleClearSelectedState(selectedFeatureRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  if (!selectedFeature) return null; // Early return if no feature is selected

  const handleClose = () => {
    handleClearSelectedState(selectedFeature);

    dispatch(setSelectedFeature(null));
  };

  const handleZoomToTarget = (geometry: Geometry) => {
    const calcBbox = bbox(geometry);

    map?.fitBounds(calcBbox as LngLatBoundsLike, {
      padding: BOUNDS_PADDING,
      maxZoom: zoomLevel,
      duration: TO_BOUNDS_DURATION
    });
  };

  const handleClickComparsion = () => {
    dispatch(resetUIDataConfigExceptSidebarMenu());
    dispatch(setSelectedDataset(currentDataset as Dataset));
    dispatch(
      setShowComparisonPopup({
        isFromAnalysisResult: showAnalysisResultPopup as boolean,
        isFromAnalysisSummary: showAnalysisSummaryPopup,
        isShow: true,
        isLimitShow: false
      })
    );
    dispatch(
      setComparisonFeature([
        {
          // No WKT put back: Compare renders its geometry row from
          // `geoJSONToWKT(feature.geometry)` and ignores the property, so it would
          // only push ~12 KB per compared feature into the persisted map slice.
          properties: selectedFeature.properties,
          id: selectedFeature?.id ?? "",
          source: selectedFeature?.source || "",
          geometry: selectedFeature.geometry
        }
      ])
    );
    showOnlyLayer(currentDataset?.data.id || "");
    handleZoomToTarget(selectedFeature.geometry);

    dispatch(setSelectedFeature(null));
    deleteAllFeatures();
    if (!isSiteProfiling) {
      hideAllMarkers();
      if (map) {
        hideAllLayersWithPrefixes(map, CATCHMENT_SOURCE_IDS);
      }
    }
  };

  const handleAttributeSelected = ({
    key,
    value,
    dataType
  }: {
    key: string;
    value: unknown;
    dataType?: DataSchemaType | DatasetSchemaLabel;
  }) => {
    const query = [String(value)];
    setSelectedAttribute({
      query: query,
      query_type: dataType
        ? [SchemaToDatasetMap[dataType as DataSchemaType]]
        : null,
      query_column: [key.toLowerCase().split(" ").join("_")]
    });

    dispatch(setSelectedDataset(currentDataset!));
    dispatch(toggleSpatialAnalysisResultTabularModal());
  };

  const transformedProperties = transformPropertiesWithCategory(
    selectedFeature?.properties as SelectedFeature,
    currentDataset?.categories ?? [],
    userProfile?.timezone
  );

  const selectedCategory = currentDataset?.selectedCategory;
  const title = transformedProperties.find(
    (item) => item.data_label === DatasetSchemaLabel.TITLE
  )?.value;

  const datasetDisplayName =
    currentDataset?.data.display_name ?? currentDataset?.data.name ?? null;
  const isPOIDataset = currentDataset?.properties?.spatialType === "poi";
  const isPOIEnhancementEnabled = isPOIInfoEnabled && isPOIDataset;

  const displayTitle =
    typeof title === "string" && title.trim().length > 0
      ? title
      : (datasetDisplayName ?? selectedFeature?.id ?? "");

  const resultTransform = transformVisibleAttributes(
    transformedProperties,
    userProfile?.timezone
  );

  const geometryCategoryIndex = currentDataset?.categories.findIndex(
    (item) =>
      item.data_label === DatasetSchemaLabel.POINT ||
      item.data_label === DatasetSchemaLabel.POLYGON ||
      item.data_label === DatasetSchemaLabel.LINE ||
      item.data_type === DataSchemaType.GEOMETRY
  );

  const attributes = selectedFeature?.properties
    ? Object.entries(selectedFeature.properties).filter(
        ([key]) => !excludedKeys.has(key)
      )
    : [];

  // At its column position: appending would drop it to the bottom whenever the
  // sort below has no category order for that key.
  if (sourceGeometry && sourceDataset) {
    attributes.splice(
      getGeometryAttributeInsertIndex(
        attributes,
        sourceDataset.columns,
        sourceGeometry.columnIndex
      ),
      0,
      [sourceGeometry.columnName, sourceGeometry.wkt]
    );
  }

  const normalizeKey = (key: string) => key.replace(/\s+/g, "_").toLowerCase();
  const attributeEntriesByNormalizedKey = new Map<
    string,
    { key: string; value: unknown; index: number }
  >();

  attributes.forEach(([key, value], index) => {
    attributeEntriesByNormalizedKey.set(normalizeKey(key), {
      key,
      value,
      index
    });
  });

  // Add Latitude and Longitude for Point geometries
  if (
    geometryCategoryIndex !== undefined &&
    geometryCategoryIndex !== -1 &&
    selectedFeature?.geometry?.coordinates &&
    selectedFeature.geometry.coordinates.length >= 2 &&
    selectedFeature.geometry.type === "Point"
  ) {
    if (geometryCategoryIndex <= attributes.length) {
      attributes.splice(geometryCategoryIndex, 0, [
        COORDINATE_LABLE.LATITUDE,
        selectedFeature.geometry.coordinates[1]
      ]);
      attributes.splice(geometryCategoryIndex + 1, 0, [
        COORDINATE_LABLE.LONGITUDE,
        selectedFeature.geometry.coordinates[0]
      ]);
    }
  }

  // Move the item with selectedCategory key to the beginning.
  if (selectedCategory) {
    const selectedItemIndex = attributes.findIndex(
      ([key]) => key === selectedCategory.attribute_name
    );
    if (selectedItemIndex > 0) {
      const selectedItem = attributes[selectedItemIndex];
      attributes.splice(selectedItemIndex, 1);
      attributes.unshift(selectedItem);
    }
  }

  const attributeTypeMap = new Map(
    (currentDataset?.categories ?? []).map((cat) => {
      const type =
        cat.data_label === DatasetSchemaLabel.ADDRESS
          ? cat.data_label
          : cat.data_type;
      return [cat.attribute_name, type];
    })
  );

  const categoryOrderMap = new Map(
    (currentDataset?.categories ?? []).map((category, index) => [
      category.attribute_name,
      index
    ])
  );

  const selectedPhotoCandidate = attributes.reduce<PhotoCandidate | null>(
    (bestCandidate, [key, value], index) => {
      const dataType = attributeTypeMap.get(key);
      if (dataType !== DataSchemaType.PHOTOS) return bestCandidate;

      const parsed = parseFeaturePhotoValue(value as string);

      if (!parsed || parsed.items.length === 0) return bestCandidate;

      const candidate: PhotoCandidate = {
        key,
        parsed,
        orderIndex: categoryOrderMap.get(key) ?? index,
        originalIndex: index,
        typePriority: getPhotoTypePriority(parsed.type)
      };

      if (!bestCandidate) return candidate;

      if (candidate.typePriority < bestCandidate.typePriority) return candidate;
      if (candidate.typePriority > bestCandidate.typePriority)
        return bestCandidate;
      if (candidate.orderIndex < bestCandidate.orderIndex) return candidate;
      if (candidate.orderIndex > bestCandidate.orderIndex) return bestCandidate;

      return candidate.originalIndex < bestCandidate.originalIndex
        ? candidate
        : bestCandidate;
    },
    null
  );

  const sortedAttributes = attributes
    .map(([key, value], index) => ({
      entry: [key, value],
      key,
      dataType: attributeTypeMap.get(key),
      orderIndex: categoryOrderMap.get(key) ?? index,
      originalIndex: index
    }))
    .sort((a, b) => {
      const priorityDiff =
        getAttributePriority(a.dataType) - getAttributePriority(b.dataType);

      if (priorityDiff !== 0) return priorityDiff;

      if (
        a.dataType === DataSchemaType.PHOTOS &&
        b.dataType === DataSchemaType.PHOTOS &&
        selectedPhotoCandidate
      ) {
        if (a.key === selectedPhotoCandidate.key) return -1;
        if (b.key === selectedPhotoCandidate.key) return 1;
      }

      const orderDiff = a.orderIndex - b.orderIndex;

      if (orderDiff !== 0) return orderDiff;

      return a.originalIndex - b.originalIndex;
    })
    .map(({ entry }) => entry);

  const currAttributes = isPOIEnhancementEnabled
    ? sortedAttributes
    : attributes;

  const ATTRIBUTE_PREVIEW_LIMIT = 6;

  let visibleAttributes = expanded
    ? currAttributes
    : currAttributes.slice(0, ATTRIBUTE_PREVIEW_LIMIT);

  if (isPOIEnhancementEnabled) {
    const addressAttribute = currAttributes.find(
      ([key]) => attributeTypeMap.get(key) === DataSchemaType.ADDRESS
    );

    if (addressAttribute) {
      const isAddressVisible = visibleAttributes.some(
        ([key]) => key === addressAttribute[0]
      );

      if (!isAddressVisible) {
        const combined = [addressAttribute, ...visibleAttributes];

        visibleAttributes = combined
          .filter(
            (entry, index, self) =>
              self.findIndex(([key]) => key === entry[0]) === index
          )
          .slice(0, ATTRIBUTE_PREVIEW_LIMIT);
      }
    }
  }

  const displayAttributes = visibleAttributes
    .map(([key, value], index) => {
      const dataType = attributeTypeMap.get(key);

      const isSelectedPhoto =
        dataType === DataSchemaType.PHOTOS &&
        selectedPhotoCandidate?.key === key;

      return {
        key,
        value: Object.prototype.hasOwnProperty.call(resultTransform, key)
          ? resultTransform[key]
          : value,
        dataType,
        originalIndex: index,
        ...(isSelectedPhoto &&
          isPOIEnhancementEnabled && {
            photos: selectedPhotoCandidate?.parsed.items
          })
      };
    })

    .filter((item) => {
      if (item.dataType === DataSchemaType.PHOTOS) {
        if (!isPOIEnhancementEnabled) return true;
        return (item.photos?.length ?? 0) > 0;
      }
      return true;
    });

  let groupedAttributes: GroupedAttributes | null = null;

  if (isPOIEnhancementEnabled) {
    // Group attributes into primary and secondary
    groupedAttributes = groupAttributesByPriority(
      displayAttributes,
      ADMIN_AREA_LABEL
    );

    // Build and add admin area attribute if available
    const adminAreaAttribute = buildAdminAreaAttribute(
      adminAreaKeys,
      attributeEntriesByNormalizedKey,
      normalizeKey,
      resultTransform
    );

    if (adminAreaAttribute) groupedAttributes.primary.push(adminAreaAttribute);

    // Sort primary attributes by type order and original position
    groupedAttributes.primary = sortPrimaryAttributes(
      groupedAttributes.primary
    );
  }

  const shouldRenderSecondary =
    expanded ||
    (!expanded &&
      groupedAttributes?.primary.length === 0 &&
      groupedAttributes?.secondary.length > 0);

  const isSpatialAnalysisResult: boolean =
    datasetDisplayName === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;

  return (
    <Draggable nodeRef={panelRef} bounds="parent">
      <div
        ref={panelRef}
        data-testid="feature-info-panel"
        className="flex flex-col absolute top-[24px] right-[5rem] bg-white rounded-xl p-4 z-[1000] max-w-[340px] w-full max-h-[90vh] overflow-hidden shadow-[0_4px_24px_5px_rgba(206,206,206,0.40)]"
      >
        <div className="flex flex-row items-start justify-between h-[40px]">
          <p
            className="text-xl font-semibold truncate text-txtHeading"
            title={String(displayTitle)}
          >
            {displayTitle}
          </p>
          <div className="flex">
            <Button
              variant="ghost"
              aria-label="Compare Feature"
              className="h-6 px-1 ml-auto fill-dim-gray"
              onClick={handleClickComparsion}
            >
              <CompareTableIcon title="Compare Feature" />
            </Button>
            <Button
              variant="ghost"
              className="h-6 px-1 ml-auto fill-dim-gray"
              onClick={handleClose}
            >
              <CloseIcon />
            </Button>
          </div>
        </div>

        <div className="flex my-2 flex-col gap-3 max-h-[90vh-100px] overflow-y-auto custom-scrollbar">
          {isPOIEnhancementEnabled && groupedAttributes ? (
            <>
              {groupedAttributes.primary.map(
                ({ key, dataType, value, photos }) => (
                  <FeatureInfoItem
                    key={key}
                    attrKey={key}
                    value={value}
                    dataType={dataType}
                    photos={photos}
                    isPOIDataset={isPOIDataset}
                    showSelectAttributeIcon={
                      !!showAnalysisResultPopup &&
                      !!isTabularViewAnalysisResultEnabled
                    }
                    onAttributeSelected={handleAttributeSelected}
                    isSpatialAnalysisResult={isSpatialAnalysisResult}
                  />
                )
              )}
              {expanded &&
                groupedAttributes.primary.length > 0 &&
                groupedAttributes.secondary.length > 0 && (
                  <div
                    className="flex-shrink-0 h-px my-2"
                    style={{ backgroundColor: "#E1E1E1" }}
                  />
                )}
              {shouldRenderSecondary &&
                groupedAttributes.secondary.map(
                  ({ key, dataType, value, photos }) => (
                    <FeatureInfoItem
                      key={key}
                      attrKey={key}
                      value={value}
                      dataType={dataType}
                      photos={photos}
                      isPOIDataset={isPOIDataset}
                      showSelectAttributeIcon={
                        !!showAnalysisResultPopup &&
                        !!isTabularViewAnalysisResultEnabled
                      }
                      onAttributeSelected={handleAttributeSelected}
                      isSpatialAnalysisResult={isSpatialAnalysisResult}
                    />
                  )
                )}
            </>
          ) : (
            displayAttributes.map(({ key, dataType, value, photos }) => (
              <FeatureInfoItem
                key={key}
                attrKey={key}
                value={value}
                dataType={dataType}
                photos={photos}
                isPOIDataset={isPOIDataset}
                showSelectAttributeIcon={
                  !!showAnalysisResultPopup &&
                  !!isTabularViewAnalysisResultEnabled
                }
                onAttributeSelected={handleAttributeSelected}
                isSpatialAnalysisResult={isSpatialAnalysisResult}
              />
            ))
          )}
        </div>
        {sortedAttributes.length > 6 && (
          <div className="flex items-center justify-center h-[30px]">
            <Button
              variant="ghost"
              className="mt-2 text-sm font-semibold"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <span>Collapse</span>
                  <ArrowUpIcon className="w-4 h-4 fill-txtBody" />
                </>
              ) : (
                <>
                  <span>Expand</span>
                  <ArrowDownIcon className="w-4 h-4 fill-txtBody" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Draggable>
  );
};
